import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { sendVerificationEmail } from '../../../UtilsEmail';

import { trackEnterEmailClickContinue } from '../../../analytics';
import { Form, FloatingLabel } from '../../../components/form';
import { Button } from '../../../components/general';

import { URL } from '../../../constants';

import { checkEmailAvailability, createUser } from '../../../services/api/auth';

import { useUserStore } from '../../../stores/user';
import { UserEmailAvailability } from '../../../types';
import { validateEmail } from '../../../utils';

import OnboardingPage from '../components/onboardingPage';

const EnterEmail: React.FC = () => {
  const { setUser } = useUserStore();

  const [email, setEmail] = useState<string>(
    sessionStorage.getItem('email') || '',
  );
  const [emailErr, setEmailErr] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailErr('');
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const emailUpd = email.toLowerCase().trim();

    const { emailErr } = validateEmail(emailUpd);

    if (emailErr) {
      setEmailErr(emailErr);
      return;
    }

    setIsLoading(true);

    try {
      const availability: UserEmailAvailability =
        await checkEmailAvailability(emailUpd);

      sessionStorage.setItem('email', emailUpd);

      trackEnterEmailClickContinue(availability);

      // accounts doesn't exist
      if (availability === 'available') {
        const res = await createUser({ email: emailUpd });
        setUser(res.user);

        const isSuccess = await sendVerificationEmail(emailUpd);
        if (isSuccess) navigate(URL.EMAIL_VERIFICATION);
      }

      // account exists and email is verified
      else if (availability === 'existing') {
        navigate(URL.LOGIN);
      }

      // account exists, but email is NOT verified
      else if (availability === 'pending') {
        const isSuccess = await sendVerificationEmail(emailUpd);
        if (isSuccess) navigate(URL.EMAIL_VERIFICATION);
      }

      // email is blocked for some reason
      else if (availability === 'unavailable') {
        setEmailErr(
          'Sorry, this email address is unavailable. Contact us for assistance or try a different email address.',
        );
      }
    } catch (err) {
      toast.error('Something went wrong. Try again later');
      console.error('Failed check email availability:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OnboardingPage title="Get Started" heading="Welcome to CrowdClip®">
      <Form onSubmit={onSubmit} disabled={isLoading}>
        <FloatingLabel
          label="Enter your email"
          name="email"
          value={email}
          onChange={onChangeEmail}
          isInvalid={!!emailErr}
          errMsg={emailErr || ''}
          required
        />

        <Button className="w-full" isSubmitBtn isProcessing={isLoading}>
          Let's Go
        </Button>
      </Form>
    </OnboardingPage>
  );
};

export default EnterEmail;
